import assert from 'assert-ts';
import type {Collection} from 'services/series/types';
import {Concept} from '../types';
import type {
  BulkUpdate,
  BulkUpdateCreate,
  BulkUpdateCreateResponse,
  BulkUpdateList,
  GetTokens,
} from './types';
import {getCollection} from './collection';
import type {Schemas} from './dto.generated';
import {exceptionToPromiseReject} from './exceptionToPromiseReject';
import {httpGet, httpPost} from './http/ebba';
import {
  mapBulkUpdateDto,
  mapBulkUpdateSummaryDto,
  mapToBulkUpdateWorkRequest,
} from './mappers';

export const getBulkUpdate = (
  id: string,
  getTokens: GetTokens,
  mock?: boolean,
): Promise<BulkUpdate> => {
  return exceptionToPromiseReject(() =>
    // Use BulkUpdateDto from Schemas when API is ready.
    httpGet<Schemas.BulkUpdateStatusResponse>({
      subdir: 'bulkupdate/{id}',
      subdirParams: {id},
      mock: !!mock,
      getTokens,
    })
      .then(r => {
        if (r.field.field !== 'seriesRelations') {
          return {
            r,
            collections: [],
          };
        }
        const collectionIds = getIdsFromAction(r.action);

        const collections: Promise<Collection>[] = [];
        collectionIds.forEach(id => {
          collections.push(getCollection.apply(null, [id, getTokens]));
        });

        return Promise.all(collections)
          .then(cols => ({
            r,
            collections: cols,
          }))
          .catch(() => {
            // If we cannot get the collections, we still return the bulk update
            // but without the collections.
            return {
              r,
              collections: [],
            };
          });
      })
      .then(({r, collections}) => {
        return mapBulkUpdateDto(id, r, collections);
      }),
  );
};

const getIdsFromAction = (
  dto:
    | Schemas.BulkUpdateActionAdd
    | Schemas.BulkUpdateActionRemove
    | Schemas.BulkUpdateActionReplace,
): string[] => {
  return ((dto as Schemas.BulkUpdateActionAdd)?.addValues ?? []).concat(
    (dto as Schemas.BulkUpdateActionRemove)?.removeValues ?? [],
    (dto as Schemas.BulkUpdateActionReplace)?.updatedValues ?? [],
  );
};

export const getBulkUpdates = (
  getTokens: GetTokens,
  page: number,
  size: number,
  mock?: boolean,
): Promise<BulkUpdateList> => {
  return exceptionToPromiseReject(() =>
    httpGet<Schemas.AllBulkUpdatesPageResponse>({
      subdir: 'bulkupdate',
      queryParams: {
        page,
        size,
        sort: 'created,desc',
      },
      mock: !!mock,
      getTokens,
    }).then(response => ({
      pages: response.totalPages ?? 0,
      elements: response.totalElements ?? 0,
      items: (response.items ?? []).map(mapBulkUpdateSummaryDto),
    })),
  );
};

export const postBulkUpdate = (
  bulkUpdate: BulkUpdateCreate,
  ids: string[],
  getTokens: GetTokens,
  mock?: boolean,
): Promise<BulkUpdateCreateResponse> => {
  return exceptionToPromiseReject(() => {
    if (bulkUpdate.type === Concept.work) {
      return httpPost<BulkUpdateCreateResponse, Schemas.BulkUpdateWorkRequest>({
        subdir: 'bulkupdate/work',
        body: mapToBulkUpdateWorkRequest(bulkUpdate, ids),
        mock: !!mock,

        getTokens,
      });
    }

    assert(false, 'postBulkUpdate: only type work implemented', bulkUpdate);
  });
};
